import { type ReactNode, useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAuth } from '@/auth/AuthProvider'
import { useTheme } from '@/theme/ThemeProvider'
import { AppIcon, type AppIconName } from '@/components/ui/AppIcon'
import { QuickAddSheet } from '@/components/log/QuickAddSheet'
import { useDiary } from '@/diary/DiaryProvider'
import { appEvents } from '@/utils/events'

// Same tabs as the mobile bar (Diary · Progress · [+] · Recipes · Settings)
// plus Saved Foods, which mobile reaches from the add-food sheet.
const NAV: { to: string; icon: AppIconName; labelKey: string }[] = [
  { to: '/diary', icon: 'today', labelKey: 'tabs.diary' },
  { to: '/progress', icon: 'stats-chart', labelKey: 'tabs.progress' },
  { to: '/saved-foods', icon: 'heart', labelKey: 'savedFoods.title' },
  { to: '/recipes', icon: 'chef-hat', labelKey: 'tabs.recipes' },
  { to: '/settings', icon: 'settings', labelKey: 'tabs.settings' },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { colorScheme, setPreference } = useTheme()
  const { selectedDate } = useDiary()
  const [sheetOpen, setSheetOpen] = useState(false)

  // Mobile emits 'openAddFood' from the center tab button; pages can too.
  useEffect(() => appEvents.on('openAddFood', () => setSheetOpen(true)), [])

  const navItems = NAV.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) =>
        clsx('flex items-center gap-md rounded-lg px-md py-sm text-body font-medium transition-colors', isActive ? 'bg-surface-inner text-primary' : 'text-text-secondary hover:bg-surface-inner hover:text-text')
      }
    >
      {({ isActive }) => (
        <>
          <AppIcon name={item.icon} filled={isActive} />
          <span>{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  ))

  return (
    <div className="flex h-full w-full flex-col bg-bg md:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="no-print hidden w-64 shrink-0 flex-col border-r border-border-light bg-surface p-md md:flex">
        <div className="mb-lg flex items-center gap-sm px-sm pt-sm">
          <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-[10px]" />
          <span className="text-h4 font-bold text-text">{t('app.name')}</span>
        </div>

        <button onClick={() => setSheetOpen(true)} className="mb-md flex items-center justify-center gap-sm rounded-lg bg-primary px-md py-smd text-button font-semibold text-on-primary hover:opacity-90">
          <AppIcon name="add" size={20} /> {t('tabs.addFood')}
        </button>

        <nav className="flex flex-1 flex-col gap-xs">{navItems}</nav>

        <div className="mt-md border-t border-border-light pt-md">
          <button onClick={() => setPreference(colorScheme === 'dark' ? 'light' : 'dark')} className="mb-sm flex w-full items-center gap-md rounded-lg px-md py-sm text-body-sm text-text-secondary hover:bg-surface-inner">
            <AppIcon name={colorScheme === 'dark' ? 'sun' : 'moon'} size={18} />
            {t('settings.theme.title')}
          </button>
          <p className="truncate px-md text-caption text-text-muted" title={user?.email ?? ''}>{user?.email}</p>
          <button onClick={() => void signOut()} className="mt-xs flex w-full items-center gap-md rounded-lg px-md py-sm text-left text-body-sm text-text-secondary hover:bg-surface-inner hover:text-risk-high">
            <AppIcon name="logout" size={18} />
            {t('settings.signOut')}
          </button>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="no-print flex items-center justify-between border-b border-border-light bg-surface px-md py-sm md:hidden">
        <span className="flex items-center gap-sm text-h4 font-bold text-text">
          <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-lg" />
          {t('app.name')}
        </span>
        <button onClick={() => navigate('/settings')} aria-label={t('tabs.settings')} className="text-text-secondary">
          <AppIcon name="settings" size={22} />
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-md py-lg pb-24 md:px-xl md:pb-lg">{children}</div>
      </main>

      {/* Bottom nav (mobile) with the center "+" */}
      <nav className="no-print fixed bottom-0 left-0 right-0 z-40 flex items-end justify-around border-t border-border-light bg-surface pb-[env(safe-area-inset-bottom)] md:hidden">
        {NAV.slice(0, 2).map((item) => <MobileTab key={item.to} item={item} />)}
        <button onClick={() => setSheetOpen(true)} aria-label={t('tabs.addFood')} className="-mt-5 mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-on-primary shadow-lg">
          <AppIcon name="add" size={28} />
        </button>
        {NAV.slice(3).map((item) => <MobileTab key={item.to} item={item} />)}
      </nav>

      <QuickAddSheet open={sheetOpen} onClose={() => setSheetOpen(false)} forDate={selectedDate} />
    </div>
  )
}

function MobileTab({ item }: { item: (typeof NAV)[number] }) {
  const { t } = useTranslation()
  return (
    <NavLink to={item.to} className={({ isActive }) => clsx('flex flex-1 flex-col items-center gap-xs py-sm text-caption', isActive ? 'text-primary' : 'text-text-muted')}>
      {({ isActive }) => (
        <>
          <AppIcon name={item.icon} filled={isActive} />
          <span>{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  )
}
